import { DpSentrySetUser, DpJurisdictionBoundaries } from '@docupet/component-library';
import { gql } from '@/api/utils';
import { default as axios, rest } from '@/api/axios';
import PetService from '@/api/services/PetService';
import CityService from '@/api/services/CityService';

export const namespaced = true;

export const state = {
  loading: false,
  neverLoaded: true,
  data: null,
  error: null,
  petOwner: null,
  pet: null,
  pets: [],
  token: null,
  duplicatePetError: false,
  duplicatePet: null,
  suggestedJurisdictions: [],
  suggestedJurisdictionsTerms: null,
  suggestedCity: null,
  suggestedJurisdiction: null,
  suggestedJurisdictionsHeaderHtml: null,
  suggestedJurisdictionsLoading: false,
  suggestedJurisdictionsBoundaries: null,
  suggestedJurisdictionsGmapsApiKey: null,
  suggestedJurisdictionsGmapsBoundaryMapId: null,
  suggestedJurisdictionsFallbackJurisdiction: null,
  suggestedJurisdictionsNonLicensingHtml: null,
  suggestedJurisdictionsNonLicensingFallbackOptionLabel: null,
  suggestedJurisdictionsNonLicensingLearnMoreHtml: null,
};

export const mutations = {
  SET_LOADING(state) {
    state.loading = true;
  },
  SET_TOKEN(state, user) {
    state.token = user.token;
    if (user.petOwner) {
      DpSentrySetUser(user.petOwner?.id, user.petOwner?.email);
    }
  },
  SAVE_PET_OWNER(state, data) {
    state.petOwner = data;
  },
  SET_PET(state, data) {
    data.profileImage = null;
    data.images = [];

    state.pet = data;
  },
  ADD_AVATAR_TO_PET(state, { image }) {
    let updatedPet = state.pet;

    const newImage = { original: image.image, id: image.id, loading: false };

    updatedPet.images.push(newImage);
    updatedPet.profileImage = newImage;

    let pet = state.pets.find((pet) => pet.id === updatedPet.id);

    pet.images.push(newImage);
    pet.profileImage = newImage;
  },
  STORE_PET(state) {
    state.pets.push(state.pet);
    state.pet = null;
  },
  success(state, data) {
    state.neverLoaded = false;
    state.loading = Boolean(data.continueLoading);
    state.data = data;
    state.error = null;
  },
  failure(state, error) {
    state.neverLoaded = false;
    state.loading = false;
    state.data = null;
    state.error = error;
  },
  clear(state) {
    state.neverLoaded = true;
    state.data = null;
    state.error = null;
    state.petOwner = null;
    state.pet = null;
    state.pets = [];
    state.token = null;
    state.duplicatePetError = false;
    state.loading = false;
  },
  SET_DUPLICATE_PET_ERROR(state, error) {
    state.duplicatePetError = error;
  },
  SET_SUGGESTED_CITY(state, city) {
    state.suggestedCity = city;
  },
  SET_SUGGESTED_JURISDICTIONS(state, suggestedJurisdictionsArray) {
    state.suggestedJurisdictions = suggestedJurisdictionsArray;
  },
  SET_SUGGESTED_JURISDICTIONS_HEADER(state, headerHtml) {
    state.suggestedJurisdictionsHeaderHtml = headerHtml;
  },
  SET_SUGGESTED_JURISDICTIONS_TERMS(state, suggestedTerms) {
    state.suggestedJurisdictionsTerms = suggestedTerms;
  },
  SET_SUGGESTED_JURISDICTION(state, suggestedJurisdiction) {
    state.suggestedJurisdiction = suggestedJurisdiction;
  },
  SET_SUGGESTED_JURISDICTIONS_LOADING(state, booleanValue) {
    state.suggestedJurisdictionsLoading = booleanValue;
  },
  SET_SUGGESTED_JURISDICTIONS_BOUNDARIES(state, boundaries) {
    state.suggestedJurisdictionsBoundaries = boundaries;
  },
  SET_SUGGESTED_JURISDICTIONS_GMAPS_API_KEY(state, apiKey) {
    state.suggestedJurisdictionsGmapsApiKey = apiKey;
  },
  SET_SUGGESTED_JURISDICTIONS_GMAPS_BOUNDARY_MAP_ID(state, mapId) {
    state.suggestedJurisdictionsGmapsBoundaryMapId = mapId;
  },
  SET_SUGGESTED_JURISDICTIONS_NON_LICENSING_LEARN_MORE_HTML(state, suggestedJurisdictionsNonLicensingLearnMoreHtml) {
    state.suggestedJurisdictionsNonLicensingLearnMoreHtml = suggestedJurisdictionsNonLicensingLearnMoreHtml;
  },
  SET_SUGGESTED_JURISDICTIONS_NON_LICENSING_FALLBACK_OPTION_LABEL(state, suggestedJurisdictionsNonLicensingFallbackOptionLabel) {
    state.suggestedJurisdictionsNonLicensingFallbackOptionLabel = suggestedJurisdictionsNonLicensingFallbackOptionLabel;
  },
  SET_SUGGESTED_JURISDICTIONS_NON_LICENSING_HTML(state, suggestedJurisdictionsNonLicensingHtml) {
    state.suggestedJurisdictionsNonLicensingHtml = suggestedJurisdictionsNonLicensingHtml;
  },
  SET_SUGGESTED_JURISDICTIONS_FALLBACK_JURISDICTION(state, fallbackJurisdiction) {
    state.suggestedJurisdictionsFallbackJurisdiction = fallbackJurisdiction;
  },
  STOP_LOADING(state) {
    state.loading = false;
  },
};

export const actions = {
  async register({ commit }, details) {
    commit('SET_LOADING');

    try {
      const query = gql`
        mutation register($details: RegistrationDetails!) {
          registerUser(details: $details) {
            token
            petOwner {
              id
              uuid
              email
              firstName
            }
          }
        }
      `;

      const variables = {
        details,
      };

      const { registerUser: results } = await axios.post('/', {
        query,
        variables,
      });
      commit('success', results);
      commit('SET_TOKEN', results);
    } catch (error) {
      commit('failure', error);
    }
  },

  async setAddresses({ commit }, { home, mailing, continueLoading, jurisdictionId = null }) {
    commit('SET_LOADING');

    try {
      const query = gql`
        mutation setAddresses($home: UserAddressInput!, $mailing: UserAddressInput, $jurisdictionId: ID) {
          setAddresses(home: $home, mailing: $mailing, jurisdictionId: $jurisdictionId) {
            id
            city {
              id
            }
            firstName
            vsLocation
          }
        }
      `;

      const variables = {
        home,
        mailing,
        jurisdictionId,
      };

      const { setAddresses: results } = await axios.post('/', {
        query,
        variables,
      });

      if (continueLoading) {
        results.continueLoading = continueLoading;
      }

      commit('success', results);
      commit('SAVE_PET_OWNER', results);
    } catch (error) {
      commit('failure', error);
    }
  },

  async load({ commit }) {
    commit('SET_LOADING');

    try {
      const query = gql`
        query {
          petOwner {
            firstName
            email
            phone
            cellPhone
            workPhone
            primaryNumberType
            city {
              country
              branding {
                phone
              }
            }
            address {
              unit
              civic
              street
              cityName
              postal
              province
              display
              additional
            }
            mailingAddress {
              unit
              civic
              street
              cityName
              postal
              province
              display
              additional
            }
          }
        }
      `;

      const { petOwner: results } = await axios.post('/', {
        query,
      });
      commit('SAVE_PET_OWNER', results);
      commit('success', results);
      return results ? Promise.resolve(results) : Promise.reject();
    } catch (error) {
      commit('failure', error);
      return Promise.reject();
    }
  },

  async addPet({ commit }, details) {
    commit('SET_LOADING');

    try {
      const query = gql`
        mutation addPet($details: AddPetDetails!) {
          addPet(petDetails: $details) {
            id
            uuid
            name
            status
            images {
              id
              original
            }
            petOwner {
              city {
                id
              }
              vsLocation
            }
          }
        }
      `;

      const variables = {
        details,
      };

      const { addPet: results } = await axios.post('/', {
        query,
        variables,
      });

      commit('SAVE_PET_OWNER', results.petOwner);

      results.petDetails = details;

      commit('SET_PET', results);
      results.continueLoading = true;
      commit('success', results);

      return results;
    } catch (exception) {
      commit('failure', exception);

      const error = exception?.message?.[0];

      if (!error) {
        return;
      }

      throw {
        name: error.extensions?.code,
        message: error.message,
      };
    }
  },

  stopLoading({ commit }) {
    commit('STOP_LOADING');
  },

  async editPet({ commit }, { pet, details, form }) {
    commit('SET_LOADING');

    try {
      const query = gql`
        mutation updatePetDetails($pet: ID!, $details: PetDetailsInput!) {
          updatePetDetails(details: $details, pet: $pet) {
            id
            name
            images {
              id
              original
            }
            petOwner {
              city {
                id
              }
              vsLocation
            }
          }
        }
      `;

      const variables = {
        details,
        pet,
      };

      const { updatePetDetails: results } = await axios.post('/', {
        query,
        variables,
      });

      results.petDetails = form;

      commit('SET_PET', results);
      commit('success', results);

      return results;
    } catch (error) {
      commit('failure', error);
    }
  },

  async storePet({ commit }) {
    commit('STORE_PET');
  },

  async uploadImage({ commit }, { pet, image }) {
    const formData = new FormData();
    formData.append('file', image);

    // let url = `https://api.local.docupet.com/api/pet-profile-image?pet=${pet}`;
    let url = `/pet-profile-image?pet=${pet}`;

    try {
      const response = await rest.post(url, formData);

      commit('ADD_AVATAR_TO_PET', { image: response.data });
    } catch (error) {
      commit('failure', error.message);
    }
  },

  async addPetOwnerContact({ commit }, input) {
    commit('SET_LOADING');
    try {
      const query = gql`
        mutation addPetOwnerContact($input: PetOwnerContactDetails!) {
          addPetOwnerContact(input: $input) {
            firstName
            vsLocation
          }
        }
      `;
      const variables = {
        input,
      };
      let continueLoading = input.continueLoading;
      if (continueLoading) {
        delete input.continueLoading;
      }
      const { addPetOwnerContact: results } = await axios.post('/', {
        query,
        variables,
      });
      if (continueLoading) {
        results.continueLoading = continueLoading;
      }
      commit('success', results);
    } catch (error) {
      commit('failure', error);
    }
  },

  async checkForDuplicatePet({ commit }, address) {
    commit('SET_DUPLICATE_PET_ERROR', null);
    try {
      await PetService.checkForDupPet(address);
      commit('SET_DUPLICATE_PET_ERROR', null);
    } catch (error) {
      commit('SET_DUPLICATE_PET_ERROR', error);
      throw error;
    }
  },
  resetState({ commit }) {
    commit('SET_LOADING', true);
    commit('clear');
  },
  async getSuggestedJurisdictions({ commit }, address) {
    try {
      commit('SET_SUGGESTED_JURISDICTIONS_LOADING', true);
      const response = await CityService.getSuggestedJurisdictions({ address });

      if (response.matchedJurisdiction) {
        commit('SET_SUGGESTED_CITY', response.matchedJurisdiction?.city);
        commit('SET_SUGGESTED_JURISDICTION', response.matchedJurisdiction?.id);

        return;
      }

      commit('SET_SUGGESTED_JURISDICTIONS', response.suggestions);
      commit('SET_SUGGESTED_JURISDICTIONS_HEADER', response.headingHtml);
      commit('SET_SUGGESTED_JURISDICTIONS_TERMS', response.suggestionTerms);

      commit('SET_SUGGESTED_JURISDICTIONS_GMAPS_API_KEY', response.gmapsApiKey);
      commit('SET_SUGGESTED_JURISDICTIONS_GMAPS_BOUNDARY_MAP_ID', response.gmapsBoundaryMapId);

      if (response.jurisdictionBoundaries) {
        const boundaries = DpJurisdictionBoundaries.fromGQL(response.jurisdictionBoundaries);
        commit('SET_SUGGESTED_JURISDICTIONS_BOUNDARIES', boundaries);
      }

      commit('SET_SUGGESTED_JURISDICTIONS_FALLBACK_JURISDICTION', response.nonLicensingFallbackJurisdiction);
      commit('SET_SUGGESTED_JURISDICTIONS_NON_LICENSING_HTML', response.nonLicensingHtml);
      commit('SET_SUGGESTED_JURISDICTIONS_NON_LICENSING_LEARN_MORE_HTML', response.nonLicensingLearnMoreHtml);
      commit('SET_SUGGESTED_JURISDICTIONS_NON_LICENSING_FALLBACK_OPTION_LABEL', response.nonLicensingFallbackOptionLabel);
    } finally {
      commit('SET_SUGGESTED_JURISDICTIONS_LOADING', false);
    }
  },
};

export const getters = {
  neverLoaded(state) {
    return state.neverLoaded;
  },
  loading(state) {
    return state.loading;
  },
  error(state) {
    return state.error;
  },
  loaded(state) {
    return !state.neverLoaded && !state.loading && (state.data || state.error);
  },
  success(state) {
    return !state.neverLoaded && !state.loading && state.data;
  },
  getToken(state) {
    return state.token;
  },
  getVsLocation(state) {
    return state.petOwner?.vsLocation;
  },
  getCityID(state) {
    return state.petOwner?.city?.id;
  },
  getAllActivePetNames: (state) => state.pets?.filter((pet) => pet.status === 'A').map((pet) => pet.name || '') || [],
  getAllInactivePetNames: (state) => state.pets?.filter((pet) => pet.status !== 'A').map((pet) => pet.name || '') || [],
  getSuggestedJurisdictionById: (state) => (id) => {
    if (!state.suggestedJurisdictions) {
      return null;
    }

    let suggestions = state.suggestedJurisdictions;

    if (state.suggestedJurisdictionsFallbackJurisdiction) {
      suggestions = suggestions.concat(state.suggestedJurisdictionsFallbackJurisdiction);
    }

    return suggestions.find((jurisdiction) => jurisdiction.id == id);
  },
};
